/*
 * Spirit of Island Cart Drawer v6 (Zaphira 8.11.1)
 * - The Shopify cart and Discount Mixer are the sources of truth.
 * - ALL in-drawer quantity/remove changes use a stable Shopify LINE ITEM KEY,
 *   never a numerical line index (indexes change as Discount Mixer adds gifts).
 * - Operations are serialized; background reconciliation cannot compete with
 *   an actively changing cart.
 * - Discount Mixer supplies the gift discount; this script only synchronizes
 *   the drawer and can add/remove the qualifying gift line when necessary.
 */
(function () {
  'use strict';
  if (window.__soiNativeCartV4Ready) return;
  var drawer = document.getElementById('mini-cart');
  if (!drawer || !drawer.matches('cart-drawer')) return;
  window.__soiNativeCartV4Ready = true;

  var giftProductId = Number(drawer.dataset.soiGiftProductId || 0);
  var giftVariantId = Number(drawer.dataset.soiGiftVariantId || 0);
  var giftThreshold = Number(drawer.dataset.soiGiftThreshold || 35000);

  var pollTimer = null;
  var syncTimer = null;
  var syncInProgress = false;
  var pendingMutations = 0;
  var mutationQueue = Promise.resolve();
  var lastCartSignature = null;
  var lastInteractionAt = 0;
  var giftAbsentSince = null;
  var lastGiftAddAttempt = 0;
  var autoRemovalCount = 0;
  var stateKey = null;
  var state = { dismissed: false, observedGift: false, manuallyPurchased: false, manualGiftLimit: null };
  var localEvent = false;
  var lastPaidGiftSubmitAt = 0;

  function root() {
    var url = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return url.endsWith('/') ? url : url + '/';
  }
  function open() { return drawer.hasAttribute('open'); }
  function updateCartOverlayVisibility() {
    // Third-party chat buttons can be inserted at the end of <body> with a
    // very high z-index. Hide their launchers while our drawer is open.
    document.body.classList.toggle('soi-cart-drawer-open', open());
  }
  function isGift(item) {
    return (giftProductId > 0 && Number(item.product_id) === giftProductId) ||
      (giftVariantId > 0 && Number(item.variant_id || item.id) === giftVariantId);
  }
  function isPaidAddOn(item) {
    return !!(item && item.properties && item.properties._soi_cart_addon === 'paid');
  }
  function qualifyingTotal(cart) {
    var giftAndNonPhysical = (cart.items || []).reduce(function (sum, item) {
      return sum + ((isGift(item) || item.requires_shipping === false) ? Number(item.final_line_price || 0) : 0);
    }, 0);
    return Math.max(0, Number(cart.total_price || 0) - giftAndNonPhysical);
  }
  // Number of COMPLETELY free units in a Shopify line. Shopify can merge
  // repeated gifts into one line, or split PDP and Discount Mixer gifts by
  // line-item properties. Partially discounted/paid boxes are not free gifts.
  function freeGiftUnits(item) {
    if (!isGift(item)) return 0;
    var quantity = Math.max(0, Number(item.quantity) || 0);
    if (!quantity) return 0;
    var finalLinePrice = Number(item.final_line_price);
    if (!Number.isFinite(finalLinePrice)) return 0;
    if (finalLinePrice === 0) return quantity;
    var originalLine = Number(item.original_line_price || 0);
    var originalUnit = Number(item.original_price || 0) || (originalLine / quantity);
    if (!originalUnit || originalUnit <= 0) return 0;
    // E.g., qty 2: 58€ original, 29€ final => exactly ONE free box.
    var freeEquivalent = Math.floor((Math.max(0, originalLine - finalLinePrice) + 1) / originalUnit);
    return Math.max(0, Math.min(quantity, freeEquivalent));
  }
  function freeGiftCount(cart) {
    return (cart.items || []).reduce(function (sum, item) { return sum + freeGiftUnits(item); }, 0);
  }
  function allowedFreeGifts(cart) {
    // At €350 one promotional box; from €700 a second is permitted, etc.
    // No additional reward tiers are advertised in the UI.
    var maxBySpend = Math.floor(qualifyingTotal(cart) / giftThreshold);
    return state.manualGiftLimit === null ? maxBySpend : Math.min(maxBySpend, state.manualGiftLimit);
  }
  function isPdpChosen(item) {
    return !!(item && item.properties && item.properties._soi_pdp_selected === 'yes');
  }
  function trimCandidate(cart) {
    var excess = freeGiftCount(cart) - allowedFreeGifts(cart);
    if (excess <= 0) return null;
    // Prefer removing the APP-generated duplicate, not a box intentionally
    // ticked on the product detail page. Explicit paid add-ons go last.
    var options = (cart.items || []).filter(function (item) { return freeGiftUnits(item) > 0; });
    options.sort(function (a, b) {
      var aPriority = isPaidAddOn(a) ? 2 : isPdpChosen(a) ? 1 : 0;
      var bPriority = isPaidAddOn(b) ? 2 : isPdpChosen(b) ? 1 : 0;
      return aPriority - bPriority;
    });
    var first = options[0];
    if (!first) return null;
    return { key: first.key, quantity: Math.max(0, Number(first.quantity) - Math.min(excess, freeGiftUnits(first))) };
  }
  function fingerprint(cart) {
    return JSON.stringify({
      total: cart.total_price,
      discount: cart.total_discount,
      items: (cart.items || []).map(function (item) {
        return [item.key, item.quantity, item.final_line_price, item.original_line_price];
      })
    });
  }
  function storageKey(cart) {
    return 'soi-gift-v4:' + String(cart.token || 'session').split('?')[0];
  }
  function readState(cart) {
    var key = storageKey(cart);
    if (stateKey === key) return;
    stateKey = key;
    state = { dismissed: false, observedGift: false, manuallyPurchased: false, manualGiftLimit: null };
    try {
      var saved = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (saved && typeof saved === 'object') {
        state.dismissed = saved.dismissed === true;
        state.observedGift = saved.observedGift === true;
        state.manuallyPurchased = saved.manuallyPurchased === true;
        // Backwards-compatible with prior v4/v5 sessions that remembered only
        // a boolean dismissal. If deliberately dismissed, award no gifts again
        // until eligibility is lost and subsequently regained.
        state.manualGiftLimit = Number.isInteger(saved.manualGiftLimit) && saved.manualGiftLimit >= 0
          ? saved.manualGiftLimit : (state.dismissed ? 0 : null);
      }
    } catch (err) { /* Storage may be blocked in privacy modes. */ }
  }
  function saveState() {
    if (!stateKey) return;
    try { sessionStorage.setItem(stateKey, JSON.stringify(state)); } catch (err) { /* optional */ }
  }
  function notify(cart) {
    lastCartSignature = fingerprint(cart);
    localEvent = true;
    try {
      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } }));
      // Zaphira uses sections, when present, to render the exact server result.
      // Otherwise it fetches the freshest Shopify mini-cart section itself.
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { cart: cart, replacementDelay: 0 }
      }));
    } finally { localEvent = false; }
  }
  function scheduleSync(delay) {
    if (!open()) return;
    if (syncTimer !== null) clearTimeout(syncTimer);
    syncTimer = setTimeout(function () { syncTimer = null; void reconcile(); }, delay == null ? 150 : delay);
  }
  function showError(message) {
    var footer = drawer.querySelector('.mini-cart__drawer-footer');
    if (!footer) return;
    var existing = footer.querySelector('[data-soi-cart-error]');
    if (!existing) {
      existing = document.createElement('p');
      existing.className = 'soi-cart__error';
      existing.setAttribute('data-soi-cart-error', '');
      existing.setAttribute('role', 'alert');
      footer.insertBefore(existing, footer.firstChild);
    }
    existing.textContent = message;
  }
  function clearError() {
    var el = drawer.querySelector('[data-soi-cart-error]');
    if (el) el.remove();
  }
  async function requestCart() {
    var response = await fetch(root() + 'cart.js', { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error('Warenkorb konnte nicht geladen werden');
    return response.json();
  }
  async function requestChange(key, quantity) {
    var response = await fetch(root() + 'cart/change.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity, sections: ['mini-cart'] })
    });
    if (!response.ok) throw new Error('Warenkorbposition konnte nicht geändert werden');
    return response.json();
  }
  async function requestGiftAdd(variantId, paid) {
    var properties = paid ? { _soi_cart_addon: 'paid' } : undefined;
    var line = { id: variantId, quantity: 1 };
    if (properties) line.properties = properties;
    var response = await fetch(root() + 'cart/add.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [line], sections: ['mini-cart'] })
    });
    if (!response.ok) throw new Error('Premium-Holzschatulle konnte nicht hinzugefügt werden');
  }
  function enqueueMutation(fn) {
    pendingMutations += 1;
    var task = mutationQueue.then(fn, fn);
    mutationQueue = task.catch(function () { /* keep queue available after error */ });
    return task.finally(function () { pendingMutations -= 1; scheduleSync(220); });
  }
  function shouldRemoveAutoGift(item, cart) {
    if (!isGift(item) || isPaidAddOn(item)) return false;
    if (state.manuallyPurchased && (cart.items || []).filter(isGift).length === 1) return false;
    // A gift at €0 is definitely promotional. Persist that identification when
    // Discount Mixer temporarily reverts its price after threshold changes.
    return Number(item.final_line_price) === 0 || state.observedGift === true;
  }

  function resetGiftCycleIfBelow(cart) {
    if (qualifyingTotal(cart) >= giftThreshold) return;
    // Clearing suppression at the MOMENT a user falls below 350 € is essential.
    // If they immediately add another item, the next qualifying period can
    // award a fresh gift without waiting for a background poll.
    state.dismissed = false;
    state.manualGiftLimit = null;
    if (!(cart.items || []).some(isGift)) {
      state.observedGift = false;
      state.manuallyPurchased = false;
      autoRemovalCount = 0;
      lastGiftAddAttempt = 0;
      giftAbsentSince = null;
    }
    saveState();
  }

  function rowBusy(row, active) {
    if (!row) return;
    row.classList.toggle('soi-cart__row-busy', active);
    row.setAttribute('aria-busy', active ? 'true' : 'false');
  }

  async function updateUserLine(row, action, amount) {
    if (row.dataset.soiPending === 'true') return;
    row.dataset.soiPending = 'true';
    rowBusy(row, true);
    lastInteractionAt = Date.now();
    try {
      await enqueueMutation(async function () {
        // Re-read at click time: line keys can change when a discount is applied.
        var cart = await requestCart();
        readState(cart);
        var originalKey = row.dataset.soiLineKey;
        var variantId = Number(row.dataset.soiVariantId);
        var item = (cart.items || []).find(function (candidate) { return candidate.key === originalKey; });
        if (!item) {
          // Never silently modify an unrelated cart line. Only recover from a
          // changed key when this variant occurs exactly once in the cart.
          var possibilities = (cart.items || []).filter(function (candidate) { return Number(candidate.variant_id) === variantId; });
          if (possibilities.length === 1) item = possibilities[0];
        }
        if (!item) {
          notify(cart);
          return;
        }
        var target = action === 'remove' ? 0 : action === 'delta' ? Math.max(0, Number(item.quantity) + amount) : Math.max(0, Math.floor(amount));
        if (!Number.isFinite(target)) return;
        if (target === Number(item.quantity)) return;
        var previousDismissed = state.dismissed;
        var previousObserved = state.observedGift;
        var previousManualGiftLimit = state.manualGiftLimit;
        var freeUnits = freeGiftUnits(item);
        if (isGift(item) && freeUnits > 0 && target < Number(item.quantity)) {
          // Explicitly reducing a free gift fixes the remaining permitted gift
          // count for THIS 350€ qualification period. At €700, removing one of
          // two gifts must not also remove the other one.
          var removedFreeUnits = Math.min(freeUnits, Number(item.quantity) - target);
          var newLimit = Math.max(0, freeGiftCount(cart) - removedFreeUnits);
          state.manualGiftLimit = state.manualGiftLimit === null ? newLimit : Math.min(state.manualGiftLimit, newLimit);
          state.dismissed = state.manualGiftLimit === 0;
          state.observedGift = true;
          saveState();
        }
        try {
          var nextCart = await requestChange(item.key, target);
          lastInteractionAt = Date.now();
          resetGiftCycleIfBelow(nextCart);
          notify(nextCart);
          clearError();
        } catch (err) {
          state.dismissed = previousDismissed;
          state.observedGift = previousObserved;
          state.manualGiftLimit = previousManualGiftLimit;
          saveState();
          throw err;
        }
      });
    } catch (err) {
      console.warn('[SOI Cart] Produktänderung:', err);
      showError('Die Änderung war nicht möglich. Bitte versuche es erneut.');
    } finally {
      row.dataset.soiPending = 'false';
      rowBusy(row, false);
    }
  }

  async function addPaidGift(button) {
    if (button.disabled) return;
    var id = Number(button.dataset.soiVariant || giftVariantId);
    if (!id) return;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    lastInteractionAt = Date.now();
    try {
      await enqueueMutation(async function () {
        var cart = await requestCart();
        readState(cart);
        if ((cart.items || []).some(isGift)) { notify(cart); return; }
        await requestGiftAdd(id, true);
        state.manuallyPurchased = true;
        saveState();
        lastInteractionAt = Date.now();
        notify(await requestCart());
      });
    } catch (err) {
      console.warn('[SOI Cart] bezahlte Schatulle:', err);
      button.disabled = false;
      button.removeAttribute('aria-busy');
      showError('Die Premium-Holzschatulle konnte nicht hinzugefügt werden.');
    }
  }

  async function reconcile() {
    if (!open() || syncInProgress || pendingMutations > 0 || Date.now() - lastInteractionAt < 150) return;
    syncInProgress = true;
    try {
      var cart = await requestCart();
      if (pendingMutations > 0) return;
      readState(cart);
      var thresholdReached = qualifyingTotal(cart) >= giftThreshold;
      var gifts = (cart.items || []).filter(isGift);
      if (gifts.some(function (item) { return freeGiftUnits(item) > 0; })) {
        state.observedGift = true;
        saveState();
      }
      if (!thresholdReached) {
        resetGiftCycleIfBelow(cart);
      }

      // HIGH PRIORITY: reconcile DUPLICATE free boxes before attempting any
      // optional additions. One free per completed 350€ order value, with the
      // box itself excluded from the eligibility calculation.
      var trimming = trimCandidate(cart);
      if (trimming && autoRemovalCount < 24) {
        autoRemovalCount += 1;
        await enqueueMutation(async function () {
          // Re-read because Discount Mixer may have changed line keys or prices
          // while the shopper was still on the PDP.
          var latest = await requestCart();
          readState(latest);
          if (qualifyingTotal(latest) < giftThreshold) resetGiftCycleIfBelow(latest);
          var currentTrim = trimCandidate(latest);
          if (!currentTrim) { notify(latest); return; }
          var next = await requestChange(currentTrim.key, currentTrim.quantity);
          notify(next);
        });
        giftAbsentSince = null;
        return;
      }
      if (!trimming) autoRemovalCount = 0;

      // Discount Mixer can leave an unmarked auto-added gift at full price
      // after a customer falls below 350€. Do not remove one specifically
      // chosen on the PDP or explicitly purchased via the cart add-on.
      if (!thresholdReached && state.observedGift) {
        var orphan = gifts.find(function (item) {
          return !isPdpChosen(item) && !isPaidAddOn(item) && freeGiftUnits(item) === 0;
        });
        if (orphan && autoRemovalCount < 24) {
          autoRemovalCount += 1;
          await enqueueMutation(async function () {
            var latest = await requestCart();
            var match = (latest.items || []).find(function (item) {
              return item.key === orphan.key && !isPdpChosen(item) && !isPaidAddOn(item);
            });
            if (!match || qualifyingTotal(latest) >= giftThreshold) return;
            notify(await requestChange(match.key, 0));
          });
          giftAbsentSince = null;
          return;
        }
      }

      if (thresholdReached && gifts.length === 0 && allowedFreeGifts(cart) > 0 && !state.manuallyPurchased && giftVariantId) {
        if (giftAbsentSince === null) giftAbsentSince = Date.now();
        // Discount Mixer normally inserts the gift itself. This fallback only
        // starts when NO box is present, never to fill a second €700 slot.
        if (Date.now() - giftAbsentSince >= 1100 && Date.now() - lastGiftAddAttempt >= 6500) {
          lastGiftAddAttempt = Date.now();
          await enqueueMutation(async function () {
            var latest = await requestCart();
            readState(latest);
            if ((latest.items || []).some(isGift) || allowedFreeGifts(latest) < 1) {
              notify(latest);
              return;
            }
            await requestGiftAdd(giftVariantId, false);
            state.observedGift = true;
            saveState();
            notify(await requestCart());
          });
          giftAbsentSince = null;
          return;
        }
        scheduleSync(Math.max(100, 1120 - (Date.now() - giftAbsentSince)));
      } else {
        giftAbsentSince = null;
      }
      var nextSignature = fingerprint(cart);
      if (nextSignature !== lastCartSignature) notify(cart);
    } catch (err) {
      console.warn('[SOI Cart] Abgleich:', err);
    } finally {
      syncInProgress = false;
    }
  }

  // Capture all cart quantity/remove actions BEFORE Zaphira's handlers. Zaphira
  // otherwise uses "line=1/2/3", which can now address another item if a
  // third-party gift appeared or disappeared during the request.
  document.addEventListener('click', function (event) {
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
    var target = event.target;
    if (!target || !target.closest) return;
    var control = target.closest('a.line-item__remove-button, a.quantity-selector__button');
    if (control && control.closest('#mini-cart')) {
      var row = control.closest('line-item[data-soi-line-key]');
      if (!row) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (control.matches('.line-item__remove-button')) {
        void updateUserLine(row, 'remove', 0);
      } else {
        // +/- semantics obtained from the signed quantity in the original URL.
        var currentInput = row.querySelector('.quantity-selector__input');
        var oldQuantity = currentInput ? Number(currentInput.value) : 1;
        var url = new URL(control.href, location.href);
        var requestedQuantity = Number(url.searchParams.get('quantity'));
        var delta = requestedQuantity < oldQuantity ? -1 : 1;
        void updateUserLine(row, 'delta', delta);
      }
      return;
    }
    var addButton = target.closest('button[data-soi-add-paid-gift]');
    if (addButton && addButton.closest('#mini-cart')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      void addPaidGift(addButton);
    }
  }, true);

  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!target || !target.matches || !target.matches('#mini-cart line-item .quantity-selector__input')) return;
    var row = target.closest('line-item[data-soi-line-key]');
    if (!row) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    void updateUserLine(row, 'set', Number(target.value));
  }, true);

  // When the same premium box was added intentionally on a PDP, do not later
  // classify this line as a disposable Discount Mixer gift.
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches('form[action*="/cart/add"]') || form.closest('#mini-cart')) return;
    var field = form.querySelector('[name="id"]');
    if (field && Number(field.value) === giftVariantId) {
      lastPaidGiftSubmitAt = Date.now();
      state.manuallyPurchased = true;
      saveState();
    }
  }, true);

  // Zaphira or a third-party app may also alter the cart. Reconcile only after
  // its own refresh event, rather than submitting a second competing update.
  document.documentElement.addEventListener('cart:updated', function (event) {
    if (!open() || localEvent) return;
    if (event.detail && event.detail.cart) lastCartSignature = fingerprint(event.detail.cart);
    scheduleSync(240);
  });
  document.documentElement.addEventListener('cart:refresh', function (event) {
    if (!open() || localEvent) return;
    if (event.detail && event.detail.cart) lastCartSignature = fingerprint(event.detail.cart);
    scheduleSync(300);
  });
  var observer = new MutationObserver(function (events) {
    if (!events.some(function (event) { return event.type === 'attributes' && event.attributeName === 'open'; })) return;
    updateCartOverlayVisibility();
    if (open()) start(); else stop();
  });
  observer.observe(drawer, { attributes: true, attributeFilter: ['open'] });

  function start() {
    if (pollTimer !== null) return;
    lastCartSignature = null;
    giftAbsentSince = null;
    scheduleSync(80);
    // Only while drawer is open. Includes app-initiated changes with no events.
    pollTimer = setInterval(function () { void reconcile(); }, 850);
  }
  function stop() {
    if (pollTimer !== null) clearInterval(pollTimer);
    if (syncTimer !== null) clearTimeout(syncTimer);
    pollTimer = null;
    syncTimer = null;
    giftAbsentSince = null;
  }
  updateCartOverlayVisibility();
  if (open()) start();

  window.SOICartQuality = {
    qualifyingTotal: qualifyingTotal,
    isGift: isGift,
    isPaidAddOn: isPaidAddOn,
    fingerprint: fingerprint,
    freeGiftUnits: freeGiftUnits,
    freeGiftCount: freeGiftCount,
    allowedFreeGifts: allowedFreeGifts,
    trimCandidate: trimCandidate
  };
})();
